import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageSquarePlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichEditor } from "@/components/ui/rich-editor";
import { useCreateThread } from "@/hooks/useForum";
import type { ThreadCategory } from "@/lib/types";

const CATEGORIES: { value: ThreadCategory; label: string }[] = [
  { value: "general",         label: "General" },
  { value: "kpi",             label: "KPI" },
  { value: "verification",    label: "Verification" },
  { value: "climate_finance", label: "Climate Finance" },
  { value: "carbon_market",   label: "Carbon Market" },
  { value: "resources",       label: "Resources" },
  { value: "announcement",    label: "Announcement" },
];

interface NewThreadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Modal new-thread composer. On success: closes, invalidates the
 *  threads list, and navigates to the new thread's detail page. */
export function NewThreadDialog({ open, onOpenChange }: NewThreadDialogProps) {
  const navigate = useNavigate();
  const create = useCreateThread();

  const [title,    setTitle]    = useState("");
  const [body,     setBody]     = useState("");
  const [category, setCategory] = useState<ThreadCategory>("general");

  useEffect(() => {
    if (!open) return;
    setTitle(""); setBody(""); setCategory("general");
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSubmit = title.trim().length > 0 && !create.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      { title: title.trim(), category, body: body.trim() || undefined },
      {
        onSuccess: (thread) => {
          onOpenChange(false);
          navigate(`/forum/threads/${thread.id}`);
        },
      },
    );
  }

  const err = create.error as
    | { response?: { data?: Record<string, unknown> | { detail?: string } } }
    | undefined;
  const errorMsg = formatApiError(err);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Start a thread</DialogTitle>
          <DialogDescription>
            Ask a question, share an update, or kick off a discussion. Visible to every
            signed-in colleague.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="nt-title">Title <span className="text-destructive">*</span></Label>
            <Input
              id="nt-title" required maxLength={200} autoFocus
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="A clear, specific question"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nt-cat">Category</Label>
            <select
              id="nt-cat"
              value={category} onChange={(e) => setCategory(e.target.value as ThreadCategory)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nt-body">Body <span className="text-xs text-muted-foreground">— optional context</span></Label>
            <RichEditor
              value={body}
              onChange={setBody}
              disabled={create.isPending}
              minHeightClass="min-h-[140px]"
              placeholder="Add background, screenshots, or any context that helps colleagues respond."
            />
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Posting…</>
                : <><MessageSquarePlus className="mr-2 h-4 w-4" />Start thread</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatApiError(
  err: { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined,
): string | null {
  if (!err?.response?.data) return null;
  const data = err.response.data as Record<string, unknown> & { detail?: string };
  if (typeof data.detail === "string") return data.detail;
  for (const msgs of Object.values(data)) {
    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
      return msgs[0] as string;
    }
  }
  return "Couldn't create the thread.";
}
